/**
 * GoalsOnboardingImport - reusable "Upload Goals" step content for the onboarding
 * Setup Checklist. Renders the multi-client GoalImportModal (fan-out by
 * client_external_id, no clientId scope) plus in-step downloads: the goals
 * template and an "export clients (with IDs)" round-trip-key CSV.
 *
 * The checklist wiring (STEP_META entry) is owned by the platform/wiring agent —
 * this component is exported cleanly so it can be slotted into that CTA.
 */

import { useState } from 'react';
import styled from 'styled-components';
import { Upload, Download, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import goalService from '../../services/api/goalService';
import { downloadBlob } from '../shared/bulkUploadUtils';
import { GoalImportModal } from './GoalImportModal';
import { downloadGoalImportTemplate } from './goalImportTemplate';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const GateBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.warning.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

interface GoalsOnboardingImportProps {
  /** Gate — the checklist already tracks UPLOAD_CLIENTS completion; pass its result here. */
  clientsUploaded: boolean;
}

export function GoalsOnboardingImport({ clientsUploaded }: GoalsOnboardingImportProps) {
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExportClients = async () => {
    setExportError(null);
    try {
      const blob = await goalService.exportClients();
      downloadBlob(blob, 'clients-export.csv');
    } catch {
      setExportError('Failed to export clients. Please try again.');
    }
  };

  return (
    <Container>
      <Description>
        Upload one CSV spanning all your clients&apos; goals. Each row&apos;s{' '}
        <code>client_external_id</code> attaches that goal to the matching client.
      </Description>

      {!clientsUploaded && (
        <GateBanner role="alert">
          <AlertTriangle />
          Upload your client roster first — goals import matches rows to clients by external ID.
        </GateBanner>
      )}

      {exportError && (
        <GateBanner role="alert">
          <AlertTriangle />
          {exportError}
        </GateBanner>
      )}

      <ActionsRow>
        <Button variant="ghost" leftIcon={<Download />} onClick={() => downloadGoalImportTemplate('multi')}>
          Download Goals Template
        </Button>
        <Button
          variant="ghost"
          leftIcon={<Download />}
          onClick={handleExportClients}
          disabled={!clientsUploaded}
        >
          Export Clients (with IDs)
        </Button>
        <Button
          variant="primary-blue"
          leftIcon={<Upload />}
          onClick={() => setShowImportModal(true)}
          disabled={!clientsUploaded}
        >
          Upload Goals
        </Button>
      </ActionsRow>

      <GoalImportModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} mode="multi" />
    </Container>
  );
}

export default GoalsOnboardingImport;
